import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { TdsAlert } from '@trv-tds/react';
import type { RootState } from '../../redux/store';
import { setSearchCategory } from '../../redux/slices/providerSearchSlice';
import { SearchForm, SearchResults } from '../../components/Search';
import { AUTO_SEARCH_TEXT, FIELDNAME, ERROR_MESSAGES } from '../../constants/textConstants';
import { AUTO_SEARCH_CONFIGS } from '../../config/providerSearchConfig';
import { getAutoNetworkSelectionOptions, getProviderStatusOptions, getStateOptions } from '../../config/updateProviderConfig';
interface AutoSearchProps {
  mode: 'external';
}

const AutoSearch: React.FC<AutoSearchProps> = ({ mode }) => {
  const dispatch = useDispatch();
  const externalSearchType = useSelector((state: RootState) => state.providerSearch.externalSearchType);


  // State for API-loaded options
  const [networkOptions, setNetworkOptions] = useState<Array<{ id: string; value: string }>>([]);
  const [approvalStatusOptions, setApprovalStatusOptions] = useState<Array<{ id: string; value: string }>>([]);
  const [stateOptions, setStateOptions] = useState<Array<{ id: string; value: string }>>([]);
  const [loadError, setLoadError] = useState<boolean>(false);


  // Set search category when component mounts
  useEffect(() => {
    dispatch(setSearchCategory('auto'));
  }, [dispatch]);


  // Load network and approval status options on mount
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [networkCodes, statusOptions, states] = await Promise.all([
          getAutoNetworkSelectionOptions(),
          getProviderStatusOptions(),
          getStateOptions()
        ]);
        setNetworkOptions(networkCodes);
        setApprovalStatusOptions(statusOptions);
        setStateOptions(states);
        setLoadError(false);
      } catch (error) {
        console.error('[AutoSearch] Error loading options:', error);
        setLoadError(true);
      }
    };
    loadOptions();
  }, []);

  // Get the appropriate form configuration based on user role & type
  let formConfig = AUTO_SEARCH_CONFIGS.external[externalSearchType]
    ? AUTO_SEARCH_CONFIGS.external[externalSearchType]
    : AUTO_SEARCH_CONFIGS.admin;

  // Clone formConfig and update options from API
  // Set initial value for networkSelection to the first available option (if any)
  const networkInitial: string = (networkOptions.find(opt => opt.id && opt.value)?.id)
    ?? (formConfig.initialValues.networkSelection as string)
    ?? '';

  formConfig = {
    ...formConfig,
    fields: formConfig.fields.map(field => {
      if (field.name === FIELDNAME.NETWORK_SELECTION) {
        return { ...field, options: networkOptions };
      }
      if (field.name === FIELDNAME.APPROVAL_STATUS) {
        return { ...field, options: approvalStatusOptions };
      }
      if (field.name === FIELDNAME.STATE) {
        return { ...field, options: stateOptions };
      }
      return field;
    }),
    initialValues: {
      ...formConfig.initialValues,
      networkSelection: networkInitial
    }
  };

  return (
    <>
      {loadError ? (
        <div className="tds-container tds-padding-top-xl tds-padding-left-xl">
          <div className="tds-row">
            <div className="tds-col-11">
              <TdsAlert
                heading="Error Alert"
                type="error"
              >
                {ERROR_MESSAGES.REFERENCE_CODE_ERROR}
              </TdsAlert>
            </div>
          </div>
        </div>
      ) : (
        <>
          <SearchForm
            currentSearchCategory="auto"
            formConfig={formConfig}
            title={AUTO_SEARCH_TEXT.PAGE_TITLE}
            description={AUTO_SEARCH_TEXT.PAGE_DESCRIPTION}
            isExternalUser={true}
          />

          <SearchResults mode={mode} />
        </>
      )}
    </>
  );
};

export default AutoSearch;